import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "page-container" }
const _hoisted_2 = { class: "page-content" }
const _hoisted_3 = { class: "flex flex-wrap gap-3 py-3" }
const _hoisted_4 = { class: "flex flex-1 gap-3" }
const _hoisted_5 = { class: "flex align-items-center" }
const _hoisted_6 = { class: "mr-2" }
const _hoisted_7 = ["textContent"]

import { computed, onMounted, ref } from 'vue';
import { useTenantStore, useAuthStore } from '@/store';
import { TenantInterface, TenantFilterInterface } from '@/types';
import { useRouting, useDateTime } from '@/composables';
import { DeleteTenantConfirm, RestoreTenantConfirm, AddTenantWizard } from '../';

interface menutItemInterface {
  label: string;
  command: () => Promise<void> | void;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'TenantList',
  setup(__props) {

const tenantStore = useTenantStore();
const authStore = useAuthStore();
const $routing = useRouting();
const showAddTenantWizard = ref(false);
const impersonateLoading = ref(false);

/**
 * Define the available default filters.
 */
const defaultFilters: TenantFilterInterface = {
  archived: false,
  search: '',
  page: '',
  per_page: 15,
};

const openDeleteConfirm = ref(false);
const openRestoreConfirm = ref(false);
const deleteId = ref();
const restoreId = ref();

/**
 * Define the filters with the default set.
 */
const filters = ref<TenantFilterInterface>({});

onMounted(() => {
  init();
});

const init = async () => {
  checkQuickbooksAuthenticationQueryParam();

  await defineFilters();
  await tenantStore.getTenants($routing.route.query);
};

const checkQuickbooksAuthenticationQueryParam = () => {
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  if (urlParams.get('quickbooks_error')) {
    const errorStr = urlParams.get('quickbooks_error');
    const error = errorStr === 'true';
    setTimeout(() => {
      tenantStore.doToast('Quickbooks authenticated', error);
    }, 1000);
  }
};

/**
 * Check whether there are any filters defined by check for query
 * params within the route object.
 */
const hasFilter = computed<boolean>(() => !!Object.keys($routing.route.query).length);

/**
 * TODO: Watch for changes to the route object in order to execute a
 * new request for data in filter etc
 */
$routing.routeChangeWatcher(async () => {
  await tenantStore.getTenants($routing.route.query);
});

/**
 * Handle the pagination of the request data.
 */
const onPage = (event) => {
  filters.value.per_page = event.rows;
  $routing.addQueryParams({ per_page: event.rows, page: event.page + 1 });
};

/**
 * Handle the ordering of the request data.
 */
const onSort = (event) => {
  const order = event.sortOrder === 1 ? 'asc' : 'desc';
  $routing.addQueryParams({ order, order_by: event.sortField });
};

const defineFilters = async () => {
  const query = $routing.route.query;

  filters.value = {
    archived: query.archived ? true : false,
    search: query.search ? (query.search as string) : '',
    page: query.page ? (query.page as string) : '',
    per_page: query.per_page ? parseInt(query.per_page as string) : defaultFilters.per_page,
  };
};

/**
 * Handle the clearing and resetting of the filters and route
 * query params.
 */
const onClearFilters = () => {
  filters.value = { ...defaultFilters };

  $routing.removeAllQueryParam();
};

/**
 * create the Action Menu model for the dataTable
 */
const createMenuModel = (item: TenantInterface) => {
  const menuItems: menutItemInterface[] = [{ label: 'Access Tenant', command: () => accessTenant(item) }];
  //if the tenant is eLuma do NOT allow deactivation
  if (item.master) return menuItems;

  const del = {
    label: 'Deactivate',
    command: () => {
      openConfirmDeleteTenant(item.id);
    },
  };

  const restore = {
    label: 'Restore',
    command: () => {
      openConfirmRestoreTenant(item.id);
    },
  };
  item.deleted_at ? menuItems.push(restore) : menuItems.push(del);
  return menuItems;
};

const accessTenant = async (tenant: TenantInterface) => {
  authStore.loading = true;
  const currentTenant = authStore.token?.tenant || undefined;
  let tenantToSwitch = authStore.token;

  if (currentTenant?.id !== tenant.id) {
    tenantToSwitch = await tenantStore.getTokenToSwitchTenant(tenant.id);
  }

  if (tenantToSwitch) await authStore.setTenantToSwitch(tenantToSwitch);
};

const openConfirmDeleteTenant = (id: number) => {
  deleteId.value = id;
  openDeleteConfirm.value = true;
};

const closeConfirmDeleteTenant = () => {
  openDeleteConfirm.value = false;
  deleteId.value = null;
};

const openConfirmRestoreTenant = (id: number) => {
  restoreId.value = id;
  openRestoreConfirm.value = true;
};

const closeConfirmRestoreTenant = () => {
  openRestoreConfirm.value = false;
  restoreId.value = null;
};

const startAddTenantWizard = () => {
  tenantStore.resetTenantStore();
  showAddTenantWizardModal();
};
const showAddTenantWizardModal = () => (showAddTenantWizard.value = true);

const closeAddTenantWizardModal = () => (showAddTenantWizard.value = false);

/**
 * Handle the filtering of the archived query.
 */
const onArchived = () => {
  if (filters.value.archived) {
    $routing.addQueryParam('archived', 'true');
  } else {
    $routing.removeQueryParam('archived');
  }
};

/**
 * Handle the filtering of results based on the provided search terms,
 * given 3 or or more characters have been provided.
 */
const onSearch = (search: string) => {
  const hasSearchTerm = search.length >= 3;

  if (hasSearchTerm) {
    $routing.addQueryParam('search', search);
    filters.value.search = search;
  } else {
    $routing.removeQueryParam('search');
  }
};

return (_ctx: any,_cache: any) => {
  const _component_Icon = _resolveComponent("Icon")!
  const _component_InputIcon = _resolveComponent("InputIcon")!
  const _component_InputText = _resolveComponent("InputText")!
  const _component_IconField = _resolveComponent("IconField")!
  const _component_InputSwitch = _resolveComponent("InputSwitch")!
  const _component_AppButton = _resolveComponent("AppButton")!
  const _component_RouterLink = _resolveComponent("RouterLink")!
  const _component_Column = _resolveComponent("Column")!
  const _component_ActionMenu = _resolveComponent("ActionMenu")!
  const _component_DataTable = _resolveComponent("DataTable")!
  const _component_Card = _resolveComponent("Card")!
  const _component_Loader = _resolveComponent("Loader")!
  const _directive_debounce = _resolveDirective("debounce")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _cache[6] || (_cache[6] = _createElementVNode("div", { class: "page-header" }, [
        _createElementVNode("h1", { class: "pb-2" }, "Tenants")
      ], -1)),
      _createElementVNode("div", _hoisted_2, [
        _createVNode(_unref(DeleteTenantConfirm), {
          id: deleteId.value,
          open: openDeleteConfirm.value,
          onUpdate: init,
          onCancel: closeConfirmDeleteTenant
        }, null, 8, ["id", "open"]),
        _createVNode(_unref(RestoreTenantConfirm), {
          id: restoreId.value,
          open: openRestoreConfirm.value,
          onUpdate: init,
          onCancel: closeConfirmRestoreTenant
        }, null, 8, ["id", "open"]),
        (showAddTenantWizard.value)
          ? (_openBlock(), _createBlock(_unref(AddTenantWizard), {
              key: 0,
              onCancel: closeAddTenantWizardModal,
              onSave: init
            }))
          : _createCommentVNode("", true),
        _createVNode(_component_Card, null, {
          content: _withCtx(() => [
            _createElementVNode("div", _hoisted_3, [
              _createElementVNode("div", _hoisted_4, [
                _createVNode(_component_IconField, { "icon-position": "left" }, {
                  default: _withCtx(() => [
                    _createVNode(_component_InputIcon, null, {
                      default: _withCtx(() => [
                        _createVNode(_component_Icon, {
                          name: "search",
                          class: "text-lg"
                        })
                      ]),
                      _: 1
                    }),
                    _withDirectives(_createVNode(_component_InputText, {
                      modelValue: filters.value.search,
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((filters.value.search) = $event)),
                      placeholder: "Search by Name"
                    }, null, 8, ["modelValue"]), [
                      [_directive_debounce, onSearch, "600ms"]
                    ])
                  ]),
                  _: 1
                }),
                _createElementVNode("div", _hoisted_5, [
                  _createElementVNode("div", _hoisted_6, [
                    _createVNode(_component_InputSwitch, {
                      modelValue: filters.value.archived,
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((filters.value.archived) = $event)),
                      onInput: onArchived
                    }, null, 8, ["modelValue"])
                  ]),
                  _cache[3] || (_cache[3] = _createElementVNode("div", { class: "text-color-secondary" }, [
                    _createElementVNode("label", null, "Show Archived")
                  ], -1))
                ]),
                (hasFilter.value)
                  ? (_openBlock(), _createBlock(_component_AppButton, {
                      key: 0,
                      type: "secondary",
                      text: "Clear Filters",
                      "icon-left": "clear",
                      onClick: onClearFilters
                    }))
                  : _createCommentVNode("", true)
              ]),
              _createVNode(_component_AppButton, {
                type: "primary",
                class: "admin-button",
                text: "Add Tenant",
                "icon-left": "add",
                onClick: startAddTenantWizard
              })
            ]),
            _createVNode(_component_DataTable, {
              filters: filters.value,
              "onUpdate:filters": _cache[2] || (_cache[2] = ($event: any) => ((filters).value = $event)),
              "filter-display": "menu",
              value: _unref(tenantStore).tenants,
              "total-records": _unref(tenantStore).tenants.length,
              "data-key": "id",
              rows: filters.value.per_page,
              lazy: true,
              paginator: true,
              "loading-icon": "logo-spinner",
              loading: _unref(tenantStore).loading,
              "rows-per-page-options": [15, 25, 30],
              "paginator-template": "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown",
              "current-page-report-template": "Showing {first} to {last} of {totalRecords}",
              "responsive-layout": "scroll",
              onPage: onPage,
              onSort: onSort
            }, {
              empty: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                _createTextVNode("No tenants found", -1)
              ]))]),
              loading: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                _createTextVNode("Loading. Please wait.", -1)
              ]))]),
              default: _withCtx(() => [
                _createVNode(_component_Column, {
                  field: "name",
                  header: "Name"
                }, {
                  body: _withCtx(({ data }) => [
                    _createVNode(_component_RouterLink, {
                      to: `/admin/tenant/${data.id}`,
                      class: "font-bold",
                      textContent: _toDisplayString(data.name)
                    }, null, 8, ["to", "textContent"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_Column, {
                  field: "city",
                  header: "City"
                }, {
                  body: _withCtx(({ data }) => [
                    _createTextVNode(_toDisplayString(data.location ? data.location.city.name : ''), 1)
                  ]),
                  _: 1
                }),
                _createVNode(_component_Column, {
                  field: "state",
                  header: "State"
                }, {
                  body: _withCtx(({ data }) => [
                    _createTextVNode(_toDisplayString(data.location ? data.location.state.name : ''), 1)
                  ]),
                  _: 1
                }),
                _createVNode(_component_Column, {
                  field: "created_at",
                  header: "Date Created"
                }, {
                  body: _withCtx(({ data }) => [
                    _createElementVNode("span", {
                      textContent: _toDisplayString(_unref(useDateTime)().formatIntlDate(data.created_at))
                    }, null, 8, _hoisted_7)
                  ]),
                  _: 1
                }),
                _createVNode(_component_Column, {
                  field: "actions",
                  header: "Actions",
                  "header-style": "width: 4em",
                  class: "text-center"
                }, {
                  body: _withCtx(({ data }) => [
                    _createVNode(_component_ActionMenu, {
                      "menu-items": createMenuModel(data),
                      icon: "more_horiz"
                    }, null, 8, ["menu-items"])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }, 8, ["filters", "value", "total-records", "rows", "loading"])
          ]),
          _: 1
        })
      ])
    ]),
    (impersonateLoading.value)
      ? (_openBlock(), _createBlock(_component_Loader, {
          key: 0,
          "full-page": true
        }))
      : _createCommentVNode("", true)
  ], 64))
}
}

})